// Gender, age, use case and tone of the catalog voices. The values are
// ElevenLabs' voice label keys, normalised; ElevenLabs labels almost every
// library voice with them (98-99% of our catalog on 2026-10-02). The voice
// picker shows the English labels below through t() (tts namespace).
// Pure constants: safe to import from client components.

export const VOICE_GENDERS = ['female', 'male', 'neutral'] as const;
export type VoiceGender = (typeof VOICE_GENDERS)[number];

export const VOICE_AGES = ['young', 'middle_aged', 'old'] as const;
export type VoiceAge = (typeof VOICE_AGES)[number];

export const VOICE_USE_CASES = [
  'conversational',
  'narrative_story',
  'informative_educational',
  'social_media',
  'characters_animation',
  'entertainment_tv',
  'advertisement',
] as const;
export type VoiceUseCase = (typeof VOICE_USE_CASES)[number];

export const GENDER_LABELS: Record<VoiceGender, string> = {
  female: 'Female',
  male: 'Male',
  neutral: 'Neutral',
};

export const AGE_LABELS: Record<VoiceAge, string> = {
  young: 'Young',
  middle_aged: 'Middle-aged',
  old: 'Older',
};

// ElevenLabs' own category names in the Voice Library.
export const USE_CASE_LABELS: Record<VoiceUseCase, string> = {
  conversational: 'Conversational',
  narrative_story: 'Narrative & Story',
  informative_educational: 'Informative & Educational',
  social_media: 'Social Media',
  characters_animation: 'Characters & Animation',
  entertainment_tv: 'Entertainment & TV',
  advertisement: 'Advertisement',
};

// Shorter names (ElevenLabs' own chip names) for the voice picker's chips and
// voice details, where the full names wrap. Search still knows both.
export const USE_CASE_SHORT_LABELS: Record<VoiceUseCase, string> = {
  conversational: 'Conversational',
  narrative_story: 'Narration',
  informative_educational: 'Educational',
  social_media: 'Social Media',
  characters_animation: 'Characters',
  entertainment_tv: 'Entertainment',
  advertisement: 'Advertisement',
};

// ElevenLabs "descriptive" labels seen on our catalog. A value not listed
// here is shown capitalised (toneLabel).
export const TONE_LABELS: Record<string, string> = {
  calm: 'Calm',
  confident: 'Confident',
  casual: 'Casual',
  professional: 'Professional',
  pleasant: 'Pleasant',
  deep: 'Deep',
  upbeat: 'Upbeat',
  gentle: 'Gentle',
  classy: 'Classy',
  chill: 'Chill',
  excited: 'Excited',
  crisp: 'Crisp',
  cute: 'Cute',
  modulated: 'Modulated',
  neutral: 'Neutral',
  formal: 'Formal',
  serious: 'Serious',
  soft: 'Soft',
  mature: 'Mature',
  relaxed: 'Relaxed',
  intense: 'Intense',
  wise: 'Wise',
  meditative: 'Meditative',
  raspy: 'Raspy',
  hyped: 'Hyped',
  whispery: 'Whispery',
  husky: 'Husky',
  rough: 'Rough',
  sassy: 'Sassy',
  robotic: 'Robotic',
  anxious: 'Anxious',
  grumpy: 'Grumpy',
  sad: 'Sad',
};

const clean = (value?: string | null) =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

export const normalizeGender = (value?: string | null): VoiceGender | null => {
  const v = clean(value);
  if (v === 'female' || v === 'woman' || v === 'f') return 'female';
  if (v === 'male' || v === 'man' || v === 'm') return 'male';
  if (v === 'neutral' || v === 'non-binary' || v === 'nonbinary') {
    return 'neutral';
  }
  return null;
};

export const normalizeAge = (value?: string | null): VoiceAge | null => {
  const v = clean(value).replace(/[\s-]+/g, '_');
  if (v === 'young' || v === 'youth' || v === 'child' || v === 'teen') {
    return 'young';
  }
  if (v === 'middle_aged' || v === 'middle' || v === 'adult') {
    return 'middle_aged';
  }
  if (v === 'old' || v === 'older' || v === 'elderly' || v === 'senior') {
    return 'old';
  }
  return null;
};

// Our older curated labels used free-form names; map them onto ElevenLabs'
// categories so the Use case filter has one value per category.
const USE_CASE_ALIASES: Record<string, VoiceUseCase> = {
  narrative: 'narrative_story',
  narration: 'narrative_story',
  storytelling: 'narrative_story',
  documentary: 'narrative_story',
  audiobook: 'narrative_story',
  informative: 'informative_educational',
  educational: 'informative_educational',
  e_learning: 'informative_educational',
  corporate_narration: 'informative_educational',
  news: 'informative_educational',
  podcast_host: 'conversational',
  podcast: 'conversational',
  characters: 'characters_animation',
  animation: 'characters_animation',
  character: 'characters_animation',
  entertainment: 'entertainment_tv',
  commercial_ad: 'advertisement',
  commercial: 'advertisement',
  ad: 'advertisement',
};

export const normalizeUseCase = (
  value?: string | null,
): VoiceUseCase | null => {
  const key = clean(value)
    .replace(/&/g, ' ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!key) return null;
  if ((VOICE_USE_CASES as readonly string[]).includes(key)) {
    return key as VoiceUseCase;
  }
  // "Narrative Story" -> narrative_story, "Entertainment TV" -> entertainment_tv
  const compact = key.replace(/_/g, '');
  const direct = VOICE_USE_CASES.find((uc) => uc.replace(/_/g, '') === compact);
  return direct || USE_CASE_ALIASES[key] || null;
};

export const normalizeTone = (value?: string | null): string | null => {
  const v = clean(value);
  return v && /^[a-z][a-z _-]{0,30}$/.test(v) ? v : null;
};

export const genderLabel = (value?: string | null) =>
  value ? GENDER_LABELS[value as VoiceGender] || null : null;

export const ageLabel = (value?: string | null) =>
  value ? AGE_LABELS[value as VoiceAge] || null : null;

export const voiceUseCaseLabel = (value?: string | null) =>
  value ? USE_CASE_LABELS[value as VoiceUseCase] || null : null;

export const voiceUseCaseShortLabel = (value?: string | null) =>
  value ? USE_CASE_SHORT_LABELS[value as VoiceUseCase] || null : null;

export const toneLabel = (value?: string | null) => {
  if (!value) return null;
  return TONE_LABELS[value] || value.charAt(0).toUpperCase() + value.slice(1);
};

// OpenAI's preset ("Saver") voices have no ElevenLabs labels.
export const OPENAI_VOICE_GENDERS: Record<string, VoiceGender> = {
  alloy: 'neutral',
  echo: 'male',
  fable: 'male',
  onyx: 'male',
  nova: 'female',
  shimmer: 'female',
};
